import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../../clients/APIClient';
import { useToastStore } from '../../../stores/ToastStore';

function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			const dataUrl = reader.result as string;
			const base64 = dataUrl.split(',')[1];
			resolve(base64);
		};
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}

export interface UseAudioRecordingOptions {
	/** State setter that receives transcribed text. */
	setTranscribedText: React.Dispatch<React.SetStateAction<string>>;
	/** Optional extra guard — return `true` to allow recording, `false` to block. */
	canRecord?: () => boolean;
	/** Called with the MediaStream right after recording starts (e.g. for waveform visualization). */
	onRecordingStart?: (stream: MediaStream) => void;
	/** Called right after the recorder stops, before transcription begins. */
	onRecordingStop?: () => void;
}

/**
 * Hook that encapsulates speech-to-text recording via the browser MediaRecorder API.
 *
 * Returns recording state and a toggle handler. Transcribed text is delivered via the
 * provided setter so it can be wired into any text input (edit textarea, chat input, etc.).
 */
export function useAudioRecording(
	optionsOrSetter:
		| UseAudioRecordingOptions
		| React.Dispatch<React.SetStateAction<string>>,
) {
	// Normalise: accept bare setter for backwards-compat (MessageContainer call-sites)
	const opts: UseAudioRecordingOptions =
		typeof optionsOrSetter === 'function'
			? { setTranscribedText: optionsOrSetter }
			: optionsOrSetter;

	const {
		setTranscribedText,
		canRecord,
		onRecordingStart,
		onRecordingStop,
	} = opts;

	const { showToast } = useToastStore();

	const [isRecording, setIsRecording] = useState(false);
	const [isTranscribing, setIsTranscribing] = useState(false);
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);

	const handleAudioClick = useCallback(async () => {
		if (isTranscribing) return;
		if (canRecord && !canRecord()) return;

		if (!isRecording) {
			try {
				const stream = await navigator.mediaDevices.getUserMedia({
					audio: true,
				});
				const recorder = new MediaRecorder(stream);
				const chunks: Blob[] = [];

				recorder.ondataavailable = (e) => {
					if (e.data.size > 0) {
						chunks.push(e.data);
					}
				};

				recorder.onstop = async () => {
					onRecordingStop?.();
					setIsTranscribing(true);

					const audioBlob = new Blob(chunks, { type: 'audio/webm' });
					const base64Audio = await blobToBase64(audioBlob);
					const audioRequest = {
						file: `'${base64Audio}'`,
						model: 'faster-whisper-tiny',
					};

					try {
						const response =
							await apiClient.audio.transcribeAudio(audioRequest);
						if (response && response.text) {
							setTranscribedText(
								(prev) =>
									prev + (prev ? ' ' : '') + response.text,
							);
						}
					} catch (err) {
						console.error('Transcription failed', err);
						showToast('Transcription failed', 'error', {
							timeout: 3000,
							placement: 'chat',
						});
					} finally {
						setIsTranscribing(false);
					}

					stream.getTracks().forEach((track) => track.stop());
				};

				recorder.start();
				mediaRecorderRef.current = recorder;
				setIsRecording(true);
				onRecordingStart?.(stream);
			} catch (error) {
				console.error('Error accessing microphone:', error);
				showToast('Could not access microphone', 'error', {
					timeout: 3000,
					placement: 'chat',
				});
			}
		} else {
			if (mediaRecorderRef.current) {
				mediaRecorderRef.current.stop();
				mediaRecorderRef.current = null;
				setIsRecording(false);
			}
		}
	}, [
		isRecording,
		isTranscribing,
		canRecord,
		onRecordingStart,
		onRecordingStop,
		setTranscribedText,
		showToast,
	]);

	// Cleanup: stop recorder on unmount
	useEffect(() => {
		return () => {
			if (mediaRecorderRef.current) {
				mediaRecorderRef.current.stop();
			}
		};
	}, []);

	return { isRecording, isTranscribing, handleAudioClick };
}
